import React, { CSSProperties, useMemo } from "react";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";
import { DimensionValue } from "../../api/models/dimension-value";
import { DimensionHierarchy } from "../../api/models/dimension-hierarchy";
import { DimensionDetail } from "../../api/models/dimension-detail";
import { getHTMLElementAsButtonProps } from "../../common/accessibility/HTMLElementAsButtonARIA";

export const MultiSelectPills = ({
  className,
  style,
  placeholder,
  onDelete,
  onClick,
  disabled,
  values,
  id,
}: {
  className?: string;
  style?: CSSProperties;
  hideClearButton?: boolean;
  placeholder?: string;
  onDelete?: (value: DimensionValue & { hierarchy: DimensionHierarchy | undefined }) => void;
  //onDelete?: (value: DimensionValue & { hierarchy: string }) => void;
  onClick?: () => void;
  disabled?: boolean;
  values: ((DimensionDetail & { isInvalid?: boolean }) | undefined)[];
  id?: string;
}) => {
  const classNames = mergeStyleSets({
    scrollableContainer: {
      width: "100%",
      minHeight: "32px",
      maxHeight: "6.85rem",
      overflowY: "auto",
    },
    pillsContainer: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "0.3rem 0.3rem",
      minHeight: "32px",
      paddingRight: "0.3rem",
      paddingTop: "0.1rem",
      paddingBottom: "0.1rem",
      cursor: disabled ? "not-allowed" : "pointer",
    },
    pill: {
      display: "flex",
      alignItems: "center",
      gap: "0.3rem",
      padding: "0.25rem 0.5rem",
      color: "white",
      backgroundColor: "var(--color-blue)",
      borderRadius: "0.5rem",
      overflow: "hidden",
      position: "relative",
      cursor: disabled ? "not-allowed !important" : "pointer",
    },
    disabledBg: {
      backgroundColor: "gray !important",
    },
    invalidBg: {
      backgroundColor: "var(--color-red)",
    },
    descriptionContainer: {
      display: "flex",
      lineHeight: "1rem",
      fontSize: "1rem",
    },
    descriptionHierarchyPart: {
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      display: "block",
      flexBasis: "auto",
    },
    descriptionValuePart: {
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      overflow: "hidden",
      display: "block",
      flexBasis: "auto",
      paddingRight: disabled ? null : "10px",
    },
    divider: {
      width: "1px",
      height: "1rem",
      backgroundColor: "white",
      marginRight: "0.5rem",
      marginLeft: "0.5rem",
    },
    placeholder: {
      height: "100%",
      lineHeight: "30px",
      color: "var(--color-disabled-text)",
      marginLeft: "0.5rem",
    },
    cross: {
      color: "white",
      fontSize: "1rem",
      lineHeight: "1rem",
      width: "18px",
      height: "1rem",
      position: "absolute",
      right: "0",
      backgroundColor: "transparent",
    },
  });

  const mapValues = useMemo(() => {
    return values?.reduce((acc, dimension) => {
      const thisDimensionValues =
        dimension?.values?.map((v) => ({ ...v, hierarchy: dimension.hierarchy, isInvalid: dimension.isInvalid })) ?? [];
      return [...acc, ...thisDimensionValues];
    }, []);
  }, [values]);

  return (
    <div
      className={classNames.scrollableContainer + ` ${className}`}
      style={style}
      {...getHTMLElementAsButtonProps({
        onClick,
      })}
      id={id}
    >
      <div className={classNames.pillsContainer}>
        {mapValues?.length ? (
          mapValues.map((v) => {
            const bgClassnames = `${disabled ? classNames.disabledBg : null} ${
              v.isInvalid ? classNames.invalidBg : null
            }`;
            return (
              <div
                key={v?.oid}
                title={(v.hierarchy ? `${v.hierarchy.desc} | ` : "") + v.desc + (v.code ? ` - ${v.code}` : "")}
                className={`${classNames.pill} ${bgClassnames}`}
                {...getHTMLElementAsButtonProps({
                  disabled: disabled,
                  onClick: (e) => e.stopPropagation(),
                })}
              >
                <div className={classNames.descriptionContainer}>
                  {v.hierarchy ? (
                    <>
                      <span className={classNames.descriptionHierarchyPart}>{v.hierarchy.desc}</span>
                      <div className={classNames.divider} />
                    </>
                  ) : null}
                  <span className={classNames.descriptionValuePart}>{v.desc}</span>
                </div>
                {disabled ? null : (
                  <span
                    className={`k-i-close ${classNames.cross}`}
                    style={{ fontFamily: "WebComponentsIcons" }}
                    {...getHTMLElementAsButtonProps({
                      onClick: (e) => {
                        if (!disabled) onDelete(v);
                        e.stopPropagation();
                      },
                    })}
                  />
                )}
              </div>
            );
          })
        ) : (
          <span className={classNames.placeholder}>{placeholder}</span>
        )}
      </div>
    </div>
  );
};
